import React, { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { Box } from '@material-ui/core';
import moment from 'moment';
import MessageEvent, { MESSAGETYPE, SENDERTYPE } from '../../chat/MessageEvent';
import ChatForm, { uploadToMXC } from '../../chat/ChatForm';
import { COLOR_GREY, COLOR_WHITE } from '../MaterialWatchman';
import FullscreenImageView from '../../chat/FullscreenImageView';
import { dataURItoBlob } from '../../chat/ChatForm';
import { MATRIX_MEMBERSHIP, MATRIX_EVENT_TYPE } from '../../../utils/ChatModule';

function isURL(str) {
    const urlTestRegex =
        /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
    const regex = new RegExp(urlTestRegex);
    try {
        return str.match(regex);
    } catch (e) {
        console.error(e);
    }
}

const useStyles = makeStyles(theme => ({
    chatLayout: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    imageChange: {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
        borderRadius: '50%',
    },
    roomNotificationUpdate: {
        color: COLOR_GREY.main,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        fontSize: theme.spacing(1.75),
        marginBottom: theme.spacing(0.5),
    },
    fileLink: {
        color: COLOR_WHITE,
        '&:visited': {
            color: COLOR_WHITE,
        },
    },
    notice: {
        marginBottom: theme.spacing(0.25),
    },
}));

const ChatLayout = ({ events, selfUserId, client, showingErrorBanner, onSendMessage, typing, onResend, onDelete }) => {
    const classes = useStyles();
    const ref = useRef(null);

    const [localclient, setLocalClient] = useState(client);
    const [maximizedImage, setMaximizedImage] = useState(null);

    const onLoadFullImage = url => {
        const maxurl = localclient.mxcUrlToHttp(url);
        setMaximizedImage(maxurl);
    };
    const closeFullscreen = () => {
        setMaximizedImage(null);
    };

    const scrollToBottom = () => {
        if (ref?.current) {
            ref.current.scrollIntoView();
        }
    };

    // if creator or joined member tied to the current MessageEvent has just joined the room
    const hasJoined = (content, membership) => {
        return (
            (content?.membership === MATRIX_MEMBERSHIP.JOIN && membership === MATRIX_MEMBERSHIP.INVITE) ||
            (content?.membership === MATRIX_MEMBERSHIP.JOIN && membership === MATRIX_MEMBERSHIP.JOIN)
        );
    };

    const isDataURI = url => {
        return typeof url === 'string' && url.indexOf('base64') > -1;
    };

    const handleResend = async event => {
        let newevent = event;
        const content = event?.event?.content;

        // if MessageEvent has unsent or non-mxc FILE attachment
        if (isDataURI(content?.url)) {
            const blob = dataURItoBlob(content?.url);
            const newMXCUrl = await uploadToMXC(client, content?.body, blob);

            // if still cannot upload to mxc, do nothing since still no connection.
            if (!newMXCUrl) {
                return;
            }

            content.url = newMXCUrl;
            newevent.event.content = content;
        }

        onResend && onResend(newevent);
    };

    // memoize handler for when an <img> has rendered its src url.
    // this scroll handler would only be assigned and ran if the last event contains an <img>
    const onImageLoaded = useCallback(() => {
        scrollToBottom();
    }, []);

    const generateMessageEvents = () => {
        const msgs = [...events];
        const proc = [];
        const dateFormat = 'MMM DD hh:mm A';

        msgs.forEach((a, key, arr) => {
            const event = a?.event;
            const { event_id: eventId, type: eventType, content } = event;
            const sender = a?.sender?.name;
            const membership = a?.sender?.membership;
            const avatarurl = a?.sender?.events?.member?.event?.content?.avatar_url;
            const title = a?.sender?.title; // TODO: will be undefined until Matrix model is updated to contain custom props
            const eventStatus = a?.status;
            let time = moment(a?.event?.localTimestamp ?? a?.event?.origin_server_ts ?? a?.localTimestamp).format(dateFormat);

            if (time.toLocaleLowerCase() === 'invalid date') {
                time = moment().format(dateFormat);
            }

            // changes to timeline messages
            if (eventType === MATRIX_EVENT_TYPE.MESSAGE) {
                let body = null;
                let msgType = MESSAGETYPE.TEXT;

                // TODO: at the moment, only image and text and urls are supported to be displayed.
                switch (content?.msgtype) {
                    case 'm.image':
                        // if url is a base64 dataURI, use it directly to display. otherwise, it is a MXC url, which we need to convert to HTTP
                        const thumburl = isDataURI(content?.url) ? content?.url : localclient.mxcUrlToHttp(content?.url, 50, 50, 'scale');
                        // if the current message event is an image and is the last in the timeline, scroll after image is loaded
                        body = <img onLoad={onImageLoaded} onClick={() => onLoadFullImage(content?.url)} src={thumburl} alt={content?.body || ''} />;
                        msgType = MESSAGETYPE.IMAGE;
                        break;
                    case 'm.file':
                        const fileurl = localclient.mxcUrlToHttp(content?.url);
                        body = (
                            <a className={classes.fileLink} href={fileurl} target="_blank" rel="noopener noreferrer">
                                {content?.body}
                            </a>
                        );
                        msgType = MESSAGETYPE.FILE;
                        break;
                    default:
                        if (!content?.body) {
                            const redactedBy = event?.unsigned?.redacted_by;
                            if (redactedBy) {
                                body = `[Message removed.]`;
                            } else return;
                        } else if (isURL(content?.body)) {
                            body = (
                                <a href={content?.body || '#'} target="_blank" rel="noopener noreferrer">
                                    {content?.body}
                                </a>
                            );
                            msgType = MESSAGETYPE.URL;
                        } else {
                            body = <span>{content?.body}</span>;
                        }
                        break;
                }

                const transformedAvatarUrl = localclient.mxcUrlToHttp(avatarurl, 20, 20, 'scale');

                proc.push(
                    <MessageEvent
                        eventId={eventId}
                        key={eventId}
                        messageType={msgType}
                        senderType={selfUserId === a?.sender?.userId ? SENDERTYPE.SELF : SENDERTYPE.OTHER}
                        sender={sender}
                        title={title}
                        avatarUrl={transformedAvatarUrl}
                        time={time}
                        deliveryState={eventStatus}
                        onResend={() => handleResend(a)}
                        onDelete={() => onDelete(a)}
                    >
                        {body}
                    </MessageEvent>
                );
            } else if (eventType === MATRIX_EVENT_TYPE.ROOM_CREATE) {
                proc.push(
                    <div key={eventId} className={classes.roomNotificationUpdate}>
                        <Box className={classes.notice} key={`room-create-${eventId}`}>
                            {`Room created.`}
                        </Box>
                    </div>
                );

                // changes to members
            } else if (eventType === MATRIX_EVENT_TYPE.ROOM_MEMBER) {
                const roomNotificationContent = [];

                if (hasJoined(content, membership)) {
                    // has just joined the room
                    roomNotificationContent.push(
                        <Box className={classes.notice} key={`room-join-${eventId}`}>
                            <strong>{content?.displayname}</strong>
                            {` joined the chat room.`}
                        </Box>
                    );
                }

                if (content?.avatar_url && avatarurl !== content?.avatar_url) {
                    const thumburl = localclient.mxcUrlToHttp(content?.avatar_url, 20, 20, 'scale');
                    roomNotificationContent.push(
                        <Box className={classes.notice} key={`avatar-change-${eventId}`}>
                            {content?.displayname} set a profile picture.
                            <img className={classes.imageChange} src={thumburl} alt={content?.displayname || ''} />
                        </Box>
                    );
                }

                if (roomNotificationContent.length) {
                    proc.push(
                        <div key={eventId} className={classes.roomNotificationUpdate}>
                            {roomNotificationContent}
                        </div>
                    );
                }
            }
        });

        return proc;
    };

    useEffect(() => {
        setLocalClient(client);
    }, [client]);

    // basic sroll handler for whenever events/error banners are updated
    useLayoutEffect(() => {
        scrollToBottom();
    }, [events, showingErrorBanner]);

    return (
        <>
            {maximizedImage && <FullscreenImageView url={maximizedImage} onClose={closeFullscreen} />}
            <Box className={classes.chatLayout}>
                {events && generateMessageEvents()}
                <div ref={ref} />
            </Box>
            <ChatForm client={client} onSendMessage={onSendMessage} typing={typing} />
        </>
    );
};

export default ChatLayout;
